import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import GhxpArrows from "./GhxpArrows";
import { Eyebrow, KineticTitle, MapLink, SceneStage, ScrollHint, Signature } from "./GhxpUi";
import { DONE_TABLE } from "./ghxpAssets";
import { EVENT_TIME, googleCalendarUrl, icsDataUri } from "./ghxpEvent";
import { canSavePass, deliverPassImage, preparePassImage } from "./ghxpPassImage";
import useSceneVideo from "./useSceneVideo";
import logoGhExperience from "../../../assets/img/ghExperience/logo-ghexperience.svg";
import yearBackground from "../../../assets/img/ghExperience/year-2026.svg";

const LOWER_PARTICLES = ["de", "da", "do", "das", "dos", "e", "del", "la", "y"];
const APPLE = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
const DAY_MS = 24 * 60 * 60 * 1000;
const SAVE_FEEDBACK_MS = 2600;

// "Maria José" -> "maria-jose" (para o nome do arquivo).
const slug = (str) =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

// Nome como o convidado digitou, sem < >, com no máximo 40 caracteres; tudo minúsculo ou tudo maiúsculo
// vira "Nome Próprio" (mantendo de/da/do/dos/das/e em minúsculas).
function normalizeName(raw) {
  const clean = String(raw || "")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40)
    .trim();
  if (clean !== clean.toLowerCase() && clean !== clean.toUpperCase()) return clean;
  return clean
    .toLowerCase()
    .split(" ")
    .map((word, i) => (i > 0 && LOWER_PARTICLES.includes(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ");
}

// Dias de calendário até o evento no horário de Brasília (UTC-3 fixo, sem horário de verão).
function daysToEvent() {
  const today = new Date(Date.now() - 3 * 60 * 60 * 1000);
  const event = new Date(EVENT_TIME - 3 * 60 * 60 * 1000);
  const day = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((day(event) - day(today)) / DAY_MS);
}

// Tela final — "seu lugar está reservado": a mesa posta da Experience House (desfocada)
// e um convite pessoal que cai na frente dela com o nome do convidado, carimbado letra a letra.
// O convite inclina com o mouse; no celular flutua e pode ser girado com o dedo. Pode ser salvo como imagem.
function GhxpDone({ formData, active, settled, near, lowPower, portrait, onRestart, onNext }) {
  const { t } = useTranslation();
  const details = t("ghxp_calendar_details");
  const name = normalizeName(formData.name);
  const first = name.split(" ")[0] || "";
  const days = daysToEvent();
  const sizeClass = name.length <= 12 ? "is-l" : name.length <= 20 ? "is-m" : "is-s";
  const sizeEm = { "is-l": 2.2, "is-m": 1.7, "is-s": 1.35 }[sizeClass];
  const step = Math.min(40, Math.round(600 / Math.max(name.length, 1)));

  const passRef = useRef(null);
  const dragRef = useRef(null);
  const scrollFrameRef = useRef(0);
  const [scrolled, setScrolled] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | busy | done | error
  const saveTimerRef = useRef(0);
  const { videoRef, playing, onPlaying } = useSceneVideo({ active, settled, warm: near, enabled: !lowPower, variant: portrait });

  // Toque: arrastar na horizontal gira o convite (até ±14°); ao soltar, ele volta com mola.
  const endDrag = () => {
    dragRef.current = null;
    const pass = passRef.current;
    if (!pass) return;
    pass.classList.remove("is-dragging");
    pass.style.removeProperty("--ghxp-mx");
  };
  const dragHandlers = {
    onPointerDown: (event) => {
      if (event.pointerType === "mouse" || lowPower) return;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragRef.current = event.clientX;
      passRef.current.classList.add("is-dragging");
    },
    onPointerMove: (event) => {
      if (dragRef.current == null) return;
      const value = Math.max(-1, Math.min(1, (event.clientX - dragRef.current) / 140));
      passRef.current.style.setProperty("--ghxp-mx", value.toFixed(3));
    },
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  };

  // Celular: ao rolar, o fundo escurece para o texto não passar sobre a toalha clara da mesa.
  const onScroll = (event) => {
    const area = event.currentTarget;
    if (scrollFrameRef.current) return;
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = 0;
      setScrolled(area.scrollTop > 24);
    });
  };

  // Imagem do convite (o mesmo cartão, sem a contagem). É gerada em segundo plano depois da entrada,
  // para o clique baixar na hora.
  const passImage = {
    name: name || t("ghxp_pass_guest"),
    sizeEm,
    labels: {
      pass: t("ghxp_pass_label"),
      guest: t("ghxp_pass_guest"),
      date: t("ghxp_pass_date"),
      dateValue: t("ghxp_pass_date_value"),
      time: t("ghxp_pass_time"),
      timeValue: t("ghxp_pass_time_value"),
      place: t("ghxp_pass_place"),
      confirmed: t("ghxp_pass_confirmed"),
      highlight: t("ghxp_done_highlight"),
    },
  };
  const passKey = JSON.stringify(passImage);
  const fileName = `${t("ghxp_pass_file")}${first ? `-${slug(first)}` : ""}.png`;

  useEffect(() => {
    if (!settled || !canSavePass()) return undefined;
    const timer = window.setTimeout(() => preparePassImage(JSON.parse(passKey)).catch(() => {}), 2400);
    return () => window.clearTimeout(timer);
  }, [settled, passKey]);

  useEffect(() => () => window.clearTimeout(saveTimerRef.current), []);

  const feedback = (state, ms) => {
    setSaveState(state);
    window.clearTimeout(saveTimerRef.current);
    if (ms) saveTimerRef.current = window.setTimeout(() => setSaveState("idle"), ms);
  };

  const save = async () => {
    if (saveState === "busy") return;
    feedback("busy");
    try {
      const blob = await preparePassImage(passImage);
      const result = await deliverPassImage(blob, fileName);
      if (result === "cancelled") feedback("idle");
      else feedback("done", SAVE_FEEDBACK_MS);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("GH Experience: não foi possível gerar a imagem do convite.", error);
      feedback("error", SAVE_FEEDBACK_MS + 800);
    }
  };

  let letter = 0;
  const calendarButtons = [
    <a
      key="google"
      className={`ghxpCalendar__button ${APPLE ? "" : "is-primary"}`}
      href={googleCalendarUrl(details)}
      target="_blank"
      rel="noreferrer"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
        <path d="M3.5 10h17M8 3v4M16 3v4M12 13v5M9.5 15.5h5" />
      </svg>
      {t("ghxp_calendar_google")}
    </a>,
    <a key="ics" className={`ghxpCalendar__button ${APPLE ? "is-primary" : ""}`} href={icsDataUri(details)} download="gh-experience.ics">
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19.5h14" />
      </svg>
      {t("ghxp_calendar_ics")}
    </a>,
  ];
  if (APPLE) calendarButtons.reverse();

  return (
    <>
      <SceneStage shade="done" arrows={false} className="ghxpDone__stage">
        <div className="ghxpDone__push">
          {near && (
            <img
              className="ghxpStage__poster"
              src={portrait ? DONE_TABLE.posterTall : DONE_TABLE.poster}
              width={portrait ? 540 : 1280}
              height={portrait ? 960 : 720}
              alt=""
              decoding="async"
            />
          )}
          {!lowPower && (
            <video
              ref={videoRef}
              key={portrait ? "tall" : "wide"}
              className={`ghxpStage__video ${playing ? "is-playing" : ""}`}
              src={portrait ? DONE_TABLE.tall : DONE_TABLE.wide}
              muted
              loop
              playsInline
              preload={near ? "auto" : "none"}
              onPlaying={onPlaying}
            />
          )}
        </div>
      </SceneStage>
      <span className={`ghxpDone__dim ${scrolled ? "is-on" : ""}`} aria-hidden="true" />
      <span className={`ghxpDone__veil ${scrolled ? "is-on" : ""}`} aria-hidden="true" />

      <div className="ghxpDone__scroll" data-scrollable="y" onScroll={onScroll}>
        <div className="ghxpDone">
          <Eyebrow className="ghxpDone__eyebrow ghxpReveal">{t("ghxp_done_eyebrow")}</Eyebrow>

          <KineticTitle
            text={first ? t("ghxp_done_greeting", { name: first }) : t("ghxp_done_greeting_anon")}
            className="ghxpDone__greeting"
            id="ghxp-done-title"
            tabIndex={-1}
          />

          <div className="ghxpPass__wrap">
            <span className="ghxpPass__shadow" aria-hidden="true" />
            <div className="ghxpPass__drop">
              <div className="ghxpPass__float">
                <figure ref={passRef} className="ghxpPass" aria-label={t("ghxp_pass_aria")} {...dragHandlers}>
                  <div className="ghxpPass__face">
                    <img className="ghxpPass__pattern" src={yearBackground} alt="" aria-hidden="true" />
                    <header className="ghxpPass__head">
                      <img src={logoGhExperience} alt="#GHEXPERIENCE" width="912" height="83" />
                      <span>{t("ghxp_pass_label")}</span>
                    </header>

                    <div className="ghxpPass__guest">
                      <span className="ghxpPass__label">{t("ghxp_pass_guest")}</span>
                      <p className={`ghxpPass__name ${sizeClass}`} style={{ "--step": `${step}ms` }}>
                        <span className="ghxpSrOnly">{name || t("ghxp_pass_guest")}</span>
                        <span aria-hidden="true">
                          {(name || t("ghxp_pass_guest"))
                            .split(" ")
                            .map((word, w) => (
                              <React.Fragment key={`${word}-${w}`}>
                                {w > 0 && " "}
                                <span className="ghxpPass__nameWord">
                                  {[...word].map((ch) => {
                                    const c = letter;
                                    letter += 1;
                                    return (
                                      <i key={c} style={{ "--c": c }}>
                                        {ch}
                                      </i>
                                    );
                                  })}
                                </span>
                              </React.Fragment>
                            ))}
                        </span>
                      </p>
                    </div>

                    <dl className="ghxpPass__grid">
                      <div>
                        <dt>{t("ghxp_pass_date")}</dt>
                        <dd>{t("ghxp_pass_date_value")}</dd>
                      </div>
                      <div>
                        <dt>{t("ghxp_pass_time")}</dt>
                        <dd>{t("ghxp_pass_time_value")}</dd>
                      </div>
                      <div className="is-wide">
                        <dt>{t("ghxp_pass_place")}</dt>
                        <dd>
                          Experience House · Rooftop
                          <small>Rua Gerivatiba, 207 · Butantã · São Paulo</small>
                        </dd>
                      </div>
                    </dl>

                    <span className="ghxpPass__tear" aria-hidden="true" />
                    <footer className="ghxpPass__stub">
                      <svg className="ghxpPass__seal" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <circle cx="12" cy="12" r="10.5" />
                        <path d="M7.5 12.5l3 3 6-6.5" />
                      </svg>
                      <p>
                        <strong>{t("ghxp_pass_confirmed")}</strong>
                        <span>{days > 0 ? t("ghxp_countdown", { count: days }) : t("ghxp_countdown_today")}</span>
                      </p>
                      <GhxpArrows variant="solid" size="sm" className="ghxpPass__mark" />
                    </footer>

                    <span className="ghxpPass__glare" aria-hidden="true" />
                    <span className="ghxpPass__sheen" aria-hidden="true" />
                  </div>
                </figure>
              </div>
            </div>
            {canSavePass() && (
              <button
                type="button"
                className={`ghxpPass__save is-${saveState}`}
                onClick={save}
                aria-busy={saveState === "busy"}
                aria-live="polite"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  {saveState === "done" ? <path d="M5.5 12.5l4 4 9-9.5" /> : <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19.5h14" />}
                </svg>
                {t(
                  {
                    idle: "ghxp_pass_save",
                    busy: "ghxp_pass_saving",
                    done: "ghxp_pass_saved",
                    error: "ghxp_pass_save_error",
                  }[saveState]
                )}
              </button>
            )}
          </div>

          <p className="ghxpDone__text ghxpReveal">{t("ghxp_done_text")}</p>
          <KineticTitle as="p" text={t("ghxp_done_highlight")} className="ghxpDone__highlight" />

          <div className="ghxpCalendar ghxpReveal">
            <p className="ghxpCalendar__title">{t("ghxp_calendar_title")}</p>
            <div className="ghxpCalendar__actions">{calendarButtons}</div>
          </div>
          <MapLink className="ghxpDone__map ghxpReveal" />
        </div>

        <div className="ghxpDone__bar ghxpReveal">
          <Signature className="ghxpDone__signature" />
          <button type="button" className="ghxpTextLink ghxpDone__back" onClick={onRestart}>
            {t("ghxp_back_home")}
          </button>
          <ScrollHint label={t("ghxp_done_next")} onClick={onNext} className="ghxpDone__hint" />
        </div>
      </div>
    </>
  );
}

export default GhxpDone;
